import { useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import { useDocOnce } from '@rainbow-modules/firebase-hooks';
import { orderBy, query } from 'firebase/firestore';
import { Customer } from 'data/firestore/agent/customer/types';
import { Conversation } from 'data/firestore/session/conversation/types';
import { Session } from 'data/firestore/session/types';
import useConversation from 'data/firestore/session/conversation/useCollection';
import useHttpQuery from 'data/firestore/useHttpQuery';
import AudioPlayer from '../../../../components/AudioPlayer';
import ConversationMessage from './message';
import { MessagesList, SectionHeader } from './styled';
import RecordingMessage from './recordingMesage';

interface SipUser {
    id: string;
    username: string;
    friendlyName?: string;
}

interface TranscriptProps {
    sessionId?: string;
    recordingId?: string;
    from?: Customer | null;
    status?: string;
    sip?: Session['sip'];
    direction?: string;
}

const Transcript = ({
    sessionId = '',
    recordingId = '',
    from,
    status = '',
    sip,
    direction = '',
}: TranscriptProps) => {
    const { agentId = '' } = useParams<{ agentId?: string }>();

    const { data: sipUsers = [] } = useHttpQuery<SipUser[]>({
        pathname: `/agents/${agentId}/sip-domains/${sip?.sipDomainSid}/users`,
        key: ['sip-users', agentId, sip?.sipDomainSid],
        queryOptions: { enabled: Boolean(agentId && sip?.sipDomainSid) },
    });

    const isSipAssistant = direction === 'outbound' && Boolean(sip);

    const assistantName = (() => {
        if (isSipAssistant) {
            const sipUser = sipUsers.find((u) => u.username === sip!.callerName);
            return sipUser?.friendlyName || sip!.callerName || 'Sara AI';
        }
        return 'Sara AI';
    })();
    const { data: messages, isLoading: isLoadingConversation } = useConversation(sessionId, {
        listQueryFn: (ref) => query(ref, orderBy('createdAt')),
        disabled: !sessionId,
        track: [sessionId],
    });

    const [postcallData, isLoadingPostCallData] = useDocOnce({
        path: `/session/${sessionId}/metadata/postcall`,
        flat: true,
        disabled: !sessionId,
    });

    const filteredMessages = messages.filter((message) => message.role === 'assistant' || message.role === 'user');

    const conversation = (
        postcallData?.metadata?.call?.conversation?.length > 0
            ? (postcallData?.metadata?.call?.conversation || []) as Conversation[]
            : filteredMessages
    ).map((message, index) => ({
        ...message,
        id: message.id || `${index}`,
    }));

    const isLoading = isLoadingPostCallData || isLoadingConversation;

    return (
        <MessagesList>
            <RenderIf isTrue={status === 'completed' && recordingId}>
                <AudioPlayer recordingId={recordingId} />
            </RenderIf>
            <SectionHeader>Transcription</SectionHeader>
            <RenderIf isTrue={status === 'in-progress'}>
                <RecordingMessage />
            </RenderIf>
            <RenderIf isTrue={isLoading}>
                <ConversationMessage
                    isLoading={isLoading}
                    assistantName={assistantName}
                    isSipAssistant={isSipAssistant}
                />
            </RenderIf>
            <RenderIf isTrue={!isLoading && conversation.length}>
                {conversation.map((message) => (
                    <ConversationMessage
                        key={message.id}
                        from={from}
                        role={message.role as string}
                        content={message.content}
                        assistantName={assistantName}
                        isSipAssistant={isSipAssistant}
                    />
                ))}
            </RenderIf>
            <RenderIf isTrue={!isLoading && !conversation.length}>
                <ConversationMessage
                    from={from}
                    content="No messages in this transcript."
                    assistantName={assistantName}
                    isSipAssistant={isSipAssistant}
                />
            </RenderIf>

        </MessagesList>
    );
};

export default Transcript;
